import React from 'react';
import ActionButton from '@westwing/ui-kit/ActionButton';
import useForm from '@westwing/ui-kit/Tools/hooks/useForm';
import FloatingLabelInput, { Hint } from '@westwing/ui-kit/FloatingLabelInput';
import NotificationBanner from '@westwing/ui-kit/NotificationBanner';
import UiKitCheckbox, { CheckboxProps } from '@westwing/ui-kit/Checkbox';
import styled from 'styled-components';
import { inputBaseCss } from '@westwing/ui-kit/typography';
import { VALIDATE_HOOK } from '../loginAndRegistration.types';
import {
    StyledParagraphCentered,
    StyledForm,
    StyledFieldset,
    StyledParagraph,
    StyledFormActions,
} from '../loginAndRegistration.style';
import WestwingPop from '../../SignupOverlay/WestwingPop';
import resetPasswordSchema from './registration.schema';
import useTranslate from 'Hooks/useTranslate';
import { useConfig } from 'AppShell/hooks';
import { replaceStringWithComponents } from 'Helpers/utils';

export type RegistrationFormValues = {
    password: string;
    email: string;
    acceptNewsletter: boolean;
};

const CheckboxLabel = styled.span`
    ${inputBaseCss}
    font-size: 14px;
`;

const CheckboxControl = styled.label`
    display: flex;
    cursor: pointer;
    ${CheckboxLabel} {
        padding: 0 7px;
    }
`;

const Checkbox = ({ label, ...props }: CheckboxProps) => (
    <CheckboxControl>
        <UiKitCheckbox {...props} />
        <CheckboxLabel>
            {replaceStringWithComponents(label ?? '', <WestwingPop forRegistration />, 'Westwing')}
        </CheckboxLabel>
    </CheckboxControl>
);
const NewsletterFieldset = styled.fieldset`
    border: none;
    padding: 16px;
    margin: 0;
    background: ${({ theme }) => theme.colors.turquoise10};
`;

const defaultValues: RegistrationFormValues = {
    password: '',
    email: '',
    acceptNewsletter: false,
};

export type RegistrationFormProps = {
    isActive?: boolean;
    isLoading?: boolean;
    values?: Partial<RegistrationFormValues>;
    onSubmit: (values: RegistrationFormValues) => void;
    hasApiError?: boolean;
    setHasApiError: React.Dispatch<React.SetStateAction<boolean>>;
};

const RegistrationForm = ({
    isActive = true,
    isLoading = false,
    values: initialValues,
    onSubmit: onFormSubmit,
    hasApiError = false,
    setHasApiError,
}: RegistrationFormProps): JSX.Element => {
    const {
        links: { agb, dataProtection },
    } = useConfig();
    const { tPlain, t } = useTranslate();
    const disableLinks = (value: string): string | undefined => (isActive ? value : undefined);
    const schema = resetPasswordSchema(tPlain);

    const { values, errors, onBlur, onSubmit, onChange, isValid, isValidated, touched } =
        useForm<RegistrationFormValues>({
            schema,
            onSubmitValid: onFormSubmit,
            initialValues: { ...defaultValues, ...initialValues },
            validateOn: [VALIDATE_HOOK.BLUR, VALIDATE_HOOK.SUBMIT],
        });

    const agbHref = disableLinks(`href="${agb}"`);
    const dataProtectionHref = disableLinks(`href="${dataProtection}"`);
    const terms = t('checkout_registration_terms_and_conditions', [
        `<a ${agbHref} target="_blank" class="ww-link">`,
        '</a>',
        `<a ${dataProtectionHref} target="_blank" class="ww-link">`,
        '</a>',
    ]);

    const isFieldValid = (field: keyof RegistrationFormValues) => {
        if (errors?.[field]) {
            return false;
        }
        if (touched[field] || isValidated(field)) {
            return true;
        }
        return undefined;
    };

    const showHint = (field: keyof RegistrationFormValues) => (touched[field] || isValidated(field)) && errors[field];

    return (
        <div>
            <StyledForm onSubmit={onSubmit} noValidate>
                <StyledFieldset disabled={!isActive}>
                    <StyledParagraphCentered>{tPlain('checkout_registration_motivation')}</StyledParagraphCentered>
                    {hasApiError && (
                        <NotificationBanner
                            bannerType="card"
                            displayType="error"
                            message={tPlain('Incorrect email or password_bella')}
                            messageId="registration_error"
                            dismissable
                            isSticky
                            onMessageDismissed={() => setHasApiError(false)}
                        />
                    )}
                    <FloatingLabelInput
                        id="register_email"
                        label={tPlain('E-Mail')}
                        value={values.email}
                        type="email"
                        name="email"
                        onChange={onChange}
                        onBlur={onBlur}
                        autoComplete="email"
                        isValid={isFieldValid('email')}
                        hint={showHint('email') && <Hint.Error as="output">{errors.email[0]}</Hint.Error>}
                    />
                    <FloatingLabelInput
                        id="register_password"
                        label={tPlain('Password')}
                        type="password"
                        name="password"
                        autoComplete="new-password"
                        value={values.password}
                        onChange={onChange}
                        onBlur={onBlur}
                        isValid={isFieldValid('password')}
                        hint={
                            isValidated('password') &&
                            !isValid('password') && <Hint.Error as="output">{errors.password[0]}</Hint.Error>
                        }
                    />
                    <div>
                        <NewsletterFieldset>
                            <Checkbox
                                id="checkbox"
                                name="acceptNewsletter"
                                label={tPlain('Registration_nl_checkbox')}
                                labelPosition="right"
                                onChange={onChange}
                                checked={values.acceptNewsletter}
                                variant="checkmark"
                            />
                        </NewsletterFieldset>
                    </div>
                    <StyledParagraph>{terms}</StyledParagraph>

                    <StyledFormActions>
                        <ActionButton type="submit" isLoading={isLoading}>
                            {tPlain('Further')}
                        </ActionButton>
                    </StyledFormActions>
                </StyledFieldset>
            </StyledForm>
        </div>
    );
};
export default RegistrationForm;
